<template>
    <div class="pt-4">
        <div class="mb-4 flex items-center justify-between space-x-2">
            <div>
                <h1 class="text-lg font-semibold text-gray-700">Doctors</h1>
                <p class="text-sm text-gray-500">Everyone offering consultations through the SAMU app.</p>
            </div>
            <UButton @click="overlays.create = true" color="black" icon="i-ph-plus-bold">
                New doctor
            </UButton>
        </div>

        <div class="mb-4 flex flex-wrap items-center justify-between gap-3">
            <div class="inline-flex items-center gap-1 rounded-lg bg-gray-100 p-1 ring-1 ring-inset ring-black/5">
                <button
                    v-for="tab in statusTabs"
                    :key="tab.label"
                    type="button"
                    @click="status = tab.value"
                    class="flex items-center gap-2 rounded-md px-3 py-1.5 text-sm font-medium transition"
                    :class="status === tab.value ? 'bg-white text-gray-900 shadow-sm ring-1 ring-black/5' : 'text-gray-500 hover:text-gray-700'"
                >
                    <span>{{ tab.label }}</span>
                    <span
                        class="min-w-5 rounded-full px-1.5 text-center text-xs tabular-nums"
                        :class="status === tab.value ? 'bg-gray-100 text-gray-700' : 'bg-gray-200/70 text-gray-500'"
                    >{{ tab.count }}</span>
                </button>
            </div>

            <div class="flex items-center space-x-2">
                <UInput
                    v-model="search"
                    type="text"
                    placeholder="Name, email or license..."
                    icon="i-ph-magnifying-glass-duotone"
                    class="w-64"
                />
                <USelectMenu
                    v-model="specialtyFilter"
                    :options="specialties"
                    option-attribute="name_en"
                    by="id"
                    searchable
                    trailing
                    :class="specialtyFilter ? 'w-56' : 'w-44'"
                    :leading-icon="specialtyFilter ? undefined : 'i-ph-first-aid-kit-duotone'"
                    :ui="{ icon: { base: '!size-4', trailing: { pointer: 'pointer-events-auto', base: '!size-4' } } }"
                >
                    <template #label>
                        <template v-if="specialtyFilter">
                            <span class="text-gray-400">Specialty:</span>
                            <span class="truncate font-medium">{{ specialtyFilter.name_en }}</span>
                        </template>
                        <span v-else class="text-gray-400">Specialty</span>
                    </template>
                    <template #trailing v-if="specialtyFilter">
                        <button @click.stop="specialtyFilter = undefined" type="button" class="flex items-center justify-center">
                            <UIcon name="i-ph-x-bold" class="size-3" />
                        </button>
                    </template>
                </USelectMenu>
            </div>
        </div>

        <div class="bg-white ring-1 ring-black/5 rounded-lg shadow-sm">
            <UTable
                :rows="rows"
                :columns="columns"
                :loading="loading && !doctors.length"
                :loading-state="{ icon: 'i-ph-circle-notch-bold', label: '' }"
                :ui="{ td: { padding: 'py-3' } }"
                @select="openDoctor"
            >
                <template #empty-state>
                    <div v-if="!hasFilters" class="flex flex-col items-center justify-center px-6 py-14 text-center">
                        <div class="flex size-12 items-center justify-center rounded-full bg-primary-50">
                            <UIcon name="i-ph-stethoscope-duotone" class="size-6 text-primary-500" />
                        </div>
                        <h3 class="mt-4 text-base font-semibold text-gray-800">No doctors yet</h3>
                        <p class="mt-1 max-w-sm text-sm text-gray-500">
                            Invite a doctor to complete their own profile, or build one yourself and publish it right away.
                        </p>
                        <UButton @click="overlays.create = true" color="black" icon="i-ph-plus-bold" class="mt-5">
                            New doctor
                        </UButton>
                    </div>
                    <div v-else class="flex flex-col items-center justify-center px-6 py-14 text-center">
                        <UIcon name="i-ph-magnifying-glass-duotone" class="size-6 text-gray-400" />
                        <p class="mt-2 text-sm text-gray-500">No doctors match these filters.</p>
                        <UButton @click="clearFilters()" color="gray" variant="ghost" size="xs" class="mt-2">
                            Clear filters
                        </UButton>
                    </div>
                </template>

                <template #doctor-data="{ row }">
                    <div class="flex items-center gap-3">
                        <UAvatar :src="photo(row)" :alt="row.full_name" size="md" />
                        <div class="min-w-0">
                            <p class="truncate font-medium text-gray-800">{{ row.title }} {{ row.full_name }}</p>
                            <p class="truncate text-sm text-gray-500">{{ row.email }}</p>
                        </div>
                    </div>
                </template>
                <template #specialty-data="{ row }">
                    <span v-if="row.specialty">{{ row.specialty.name_en }}</span>
                    <span v-else class="text-gray-400">Not set</span>
                </template>
                <template #session_price_usd-data="{ row }">
                    <span v-if="Number(row.session_price_usd) > 0" class="tabular-nums">{{ usd.format(Number(row.session_price_usd)) }}</span>
                    <span v-else class="text-gray-400">Not set</span>
                </template>
                <template #status-data="{ row }">
                    <TelemedicineDoctorStatusBadge :status="row.status" />
                </template>
                <template #onboarding-data="{ row }">
                    <div v-if="row.onboarding_path === DoctorOnboardingPath.invite" class="flex items-center gap-1.5 text-sm">
                        <UIcon name="i-ph-envelope-simple-duotone" class="size-4 text-gray-400" />
                        <span class="text-gray-700">Invited</span>
                        <span v-if="row.invited_at" class="text-gray-400">· {{ dayjs(row.invited_at).fromNow() }}</span>
                    </div>
                    <div v-else class="flex items-center gap-1.5 text-sm">
                        <UIcon name="i-ph-user-gear-duotone" class="size-4 text-gray-400" />
                        <span class="text-gray-700">Built by admin</span>
                    </div>
                </template>
                <template #updated_at-data="{ row }">
                    <span class="whitespace-nowrap text-sm text-gray-500" :title="dayjs(row.updated_at).format('lll')">{{ dayjs(row.updated_at).format('ll') }}</span>
                </template>
                <template #actions-data="{ row }">
                    <div @click.stop>
                        <UDropdown :items="actions(row)" :popper="{ placement: 'bottom-end' }">
                            <UButton color="gray" variant="ghost" icon="i-heroicons-ellipsis-horizontal-20-solid" />
                        </UDropdown>
                    </div>
                </template>
            </UTable>
        </div>

        <OverlayTelemedicineNewDoctor v-model:open="overlays.create" :specialties="specialties" :specialties-unavailable="specialtiesFailed" @reload="fetchData" />
        <TelemedicineSuspendDoctorModal v-model:open="overlays.suspend" :doctor="selected" @reload="fetchData" />
    </div>
</template>

<script setup lang="ts">
import dayjs from 'dayjs'
import LocalizedFormat from 'dayjs/plugin/localizedFormat'
import RelativeTime from 'dayjs/plugin/relativeTime'
import { watchDebounced } from '@vueuse/core'
import { UserRole } from '~/types/user'
import { DoctorOnboardingPath, DoctorStatus, type DoctorProfile, type Specialty } from '~/types/telemedicine'
import { canTransition, doctorPhotoUrl, publishBlockers } from '~/utils/telemedicineDoctor'

dayjs.extend(LocalizedFormat)
dayjs.extend(RelativeTime)

definePageMeta({ layout: 'default', middleware: 'auth', roles: [UserRole.admin, UserRole.telemedicineAdmin] })
useHead({ title: 'Telemedicine – Doctors' })

const client = useSupabaseClient()
const config = useRuntimeConfig()
const toast = useToast()
const { changeStatus, resendAccessEmail } = useDoctorActions()

const loading = ref(false)
const search = ref('')
const status = ref<DoctorStatus>()
const specialtyFilter = ref<Specialty>()
const doctors = ref<DoctorProfile[]>([])
const specialties = ref<Specialty[]>([])
const specialtiesFailed = ref(false)
const selected = ref<DoctorProfile>()

const overlays = reactive({
    create: false,
    suspend: false,
})

const usd = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' })

const columns = [
    { key: 'doctor', label: 'Doctor' },
    { key: 'specialty', label: 'Specialty' },
    { key: 'session_price_usd', label: 'Price' },
    { key: 'status', label: 'Status' },
    { key: 'onboarding', label: 'Onboarding' },
    { key: 'updated_at', label: 'Updated' },
    { key: 'actions', class: 'w-px' },
]

const statusTabs = computed(() => [
    { label: 'All', value: undefined, count: doctors.value.length },
    { label: 'Published', value: DoctorStatus.published, count: countOf(DoctorStatus.published) },
    { label: 'Draft', value: DoctorStatus.draft, count: countOf(DoctorStatus.draft) },
    { label: 'Suspended', value: DoctorStatus.suspended, count: countOf(DoctorStatus.suspended) },
])

const rows = computed(() => (status.value ? doctors.value.filter((d) => d.status === status.value) : doctors.value))

const hasFilters = computed(() => !!(search.value || specialtyFilter.value || status.value))

const actions = (doctor: DoctorProfile) => {
    const statusItems = []
    if (canTransition(doctor.status, DoctorStatus.published)) {
        statusItems.push({
            label: 'Publish',
            icon: 'i-ph-broadcast-duotone',
            disabled: publishBlockers(doctor).length > 0,
            click: () => setStatus(doctor, DoctorStatus.published),
        })
    }
    if (doctor.status === DoctorStatus.published) {
        statusItems.push({ label: 'Unpublish', icon: 'i-ph-eye-slash-duotone', click: () => setStatus(doctor, DoctorStatus.draft) })
    }
    if (doctor.status === DoctorStatus.suspended) {
        statusItems.push({ label: 'Reinstate to draft', icon: 'i-ph-arrow-counter-clockwise-duotone', click: () => setStatus(doctor, DoctorStatus.draft) })
    }
    if (canTransition(doctor.status, DoctorStatus.suspended)) {
        statusItems.push({
            label: 'Suspend',
            icon: 'i-ph-prohibit-duotone',
            iconClass: 'text-red-400',
            click: () => {
                selected.value = doctor
                overlays.suspend = true
            },
        })
    }

    return [
        [{ label: 'Open', icon: 'i-ph-arrow-square-out-duotone', click: () => openDoctor(doctor) }],
        statusItems,
        [{ label: 'Resend access email', icon: 'i-ph-envelope-simple-duotone', click: () => resend(doctor) }],
    ]
}

onMounted(() => {
    fetchSpecialties()
    fetchData()
})
watchDebounced(search, () => fetchData(), { debounce: 400 })
watch(specialtyFilter, () => fetchData())

function countOf(value: DoctorStatus) {
    return doctors.value.filter((d) => d.status === value).length
}

function photo(doctor: DoctorProfile) {
    return doctorPhotoUrl(config.public.supabase.url, doctor) ?? undefined
}

async function fetchSpecialties() {
    const { data, error } = await client.from('specialties').select('*').order('sort_order').order('name_en')
    specialtiesFailed.value = !!error
    if (error) {
        toast.add({ title: 'Could not load specialties', description: error.message, icon: 'i-ph-warning-duotone', color: 'red' })
        return
    }
    specialties.value = (data ?? []) as unknown as Specialty[]
}

let latestRequest = 0

async function fetchData() {
    const request = ++latestRequest
    loading.value = true

    let query = client.from('doctor_profiles')
        .select('*, specialty:specialties(id, name_en, name_ar)')
        .order('full_name')

    const term = search.value.replace(/[%,()*\\]/g, ' ').trim()
    if (term) {
        query = query.or(`full_name.ilike.%${term}%,email.ilike.%${term}%,license_number.ilike.%${term}%`)
    }

    if (specialtyFilter.value) {
        query = query.eq('specialty_id', specialtyFilter.value.id)
    }

    const { data, error } = await query

    if (request !== latestRequest) return

    loading.value = false

    if (error) {
        toast.add({ title: 'Could not load doctors', description: error.message, icon: 'i-ph-warning-duotone', color: 'red' })
        return
    }

    doctors.value = (data ?? []) as unknown as DoctorProfile[]
}

function openDoctor(doctor: DoctorProfile) {
    navigateTo(`/telemedicine/doctors/${doctor.id}`)
}

async function setStatus(doctor: DoctorProfile, next: DoctorStatus) {
    if (await changeStatus(doctor, next)) await fetchData()
}

async function resend(doctor: DoctorProfile) {
    if (await resendAccessEmail(doctor)) await fetchData()
}

function clearFilters() {
    search.value = ''
    status.value = undefined
    specialtyFilter.value = undefined
}
</script>
